import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {ConvoFile, Step} from '../../../models/convo';
import {StepId, UUID} from '../../../models/uuid';
import {Observable, Subscription} from 'rxjs';
import {Alias} from '../../../models/llm/llm';
import {NgClass} from '@angular/common';
import {MarkdownComponent} from 'ngx-markdown';
import {MotiDataSource} from 'src/moti/models/moti-data-source';
import {DataSourceService} from 'src/moti/services/data-source.service';
import {User} from '../../../../shared/models/user';
import {MotiUrlService} from 'src/moti/services/moti-url.service';
import {InfoCircleComponent} from '../../info-circle/info-circle.component';
import {ConvoStepMetadataComponent} from '../convo-step-metadata/convo-step-metadata.component';
import {DownloadSourceIconComponent} from '../../download-source-icon/download-source-icon.component';
import {SummaryLoaderComponent} from '../../summary-loader/summary-loader.component';


@Component({
  selector: 'moti-convo-bubble',
  templateUrl: './convo-bubble.component.html',
  styleUrl: './convo-bubble.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MarkdownComponent,
    InfoCircleComponent,
    ConvoStepMetadataComponent,
    NgClass,
    DownloadSourceIconComponent,
    SummaryLoaderComponent,
  ],
})
export class ConvoBubbleComponent implements OnInit, OnDestroy {
  @Input() step$: Observable<Step>;
  @Input() user: User;
  @Input() rtl: boolean;
  @Input() stagger: boolean = true;
  @Input() showModel: boolean = false;
  @Input() showMetadata: boolean = true;
  @Input() isLatestStep: boolean = false;

  /**
   * Defines whether the various actions, like add and delete, should be made available yet
   */
  @Input() readyToAction: boolean = true;

  @Input() deletingStep: boolean;
  @Output() clickedDelete$: EventEmitter<StepId> = new EventEmitter<StepId>();

  @Input() addingStep: boolean;
  @Output() clickedAdd$: EventEmitter<Alias> = new EventEmitter<Alias>();

  dataSourceMap: { [id: UUID]: MotiDataSource } = {}; // used for file steps

  private sub: Subscription;
  step: Step;
  text: string;
  files: ConvoFile[];

  canViewDataSources: boolean;


  constructor(
    private dataSourceSvc: DataSourceService,
  ) {
  }


  ngOnInit() {
    this.sub = this.step$.subscribe({
      next: step => {
        this.handleStep(step);
      },
    });
  }


  ngOnDestroy() {
    this.sub?.unsubscribe();
  }


  /**
   * Steps incoming via this.steps$ should be sent here.
   *
   * @param step
   * @private
   */
  private handleStep(step: Step) {
    // Update rtl when we get the step if it wasn't set
    if (this.rtl === undefined && step && this.user) {
      this.rtl = step.createdBy == this.user.ID;
    }
    
    // The files array doesn't ever stream, so we only ever need to check for
    // this if this.step is undefined
    if (!this.step?.files?.length && step.files?.length) {
      this.canViewDataSources = true;

      // TODO: need a caching mechanism of some kind because multiple file bubbles will all be calling
      //       same exact API
      this.dataSourceSvc.queryDataSources$({convoId: step.convoId}).subscribe({
        next: (dss) => {
          dss.map(ds => this.dataSourceMap[ds.id] = ds);
          this.mergeStep(step);
        },
        error: err => {
          console.error(err);
          this.mergeStep(step);
        },
      });
    }

    this.mergeStep(step);
  }


  /**
   * For when this.step is already defined but we have updated info, this method
   * merges the incoming step's data into this.step. This also handles for when
   * this.step
   *
   * @param step
   * @private
   */
  private mergeStep(step: Step) {
    if (!step) {
      return;
    }

    if (!this.step) {
      this.step = step;
    }

    this.step = step;

    if (step.text) {
      this.text = this.prepMarkdownText(step);
    }

    this.files = step.files;
  }


  private prepMarkdownText(step: Step): string {
    let text = step.text;

    for (let i in step.annotations) {
      const anno = step.annotations[i];

      text = text.replace(anno.refMark, ``);
    }

    return text;
  }


  protected readonly MotiUrlService = MotiUrlService;
}
